import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { getApiUrl } from "./apiUrl";

const API_BASE_URL = `${getApiUrl()}/api/v1`;

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

function processQueue(error: AxiosError | null, token: string | null = null) {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
}

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem("access_token");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem("refresh_token");

      if (!refreshToken) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("auth_provider");
        sessionStorage.setItem("auth:session_expired", "1");
        // Don't redirect if on login page (may have OAuth callback params)
        if (!window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
          refreshToken,
        });

        const { accessToken, refreshToken: newRefreshToken } = response.data.data;

        localStorage.setItem("access_token", accessToken);
        localStorage.setItem("refresh_token", newRefreshToken);

        processQueue(null, accessToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        // For Google users: attempt silent FedCM re-auth before giving up
        const authProvider = localStorage.getItem("auth_provider");
        if (authProvider === "GOOGLE") {
          try {
            const { getSilentCredential } = await import("./fedcm");
            const credential = await getSilentCredential();
            if (credential?.type === "identity" && credential.token) {
              const silentRes = await axios.post(`${API_BASE_URL}/auth/google`, {
                idToken: credential.token,
              });
              if (silentRes.data?.data) {
                const { accessToken: newAt, refreshToken: newRt } = silentRes.data.data;
                localStorage.setItem("access_token", newAt);
                localStorage.setItem("refresh_token", newRt);
                processQueue(null, newAt);
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${newAt}`;
                }
                isRefreshing = false;
                return api(originalRequest);
              }
            }
          } catch {
            // Google silent auth also failed — fall through to redirect
          }
        }

        processQueue(refreshError as AxiosError, null);
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("auth_provider");
        // Signal login page to show session-expired toast
        sessionStorage.setItem("auth:session_expired", "1");
        // Don't redirect if on login page (may have OAuth callback params)
        if (!window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export interface ApiResponse<T> {
  data: T;
  message?: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ApiError {
  message: string;
  statusCode: number;
  errors?: Record<string, string[]>;
}

interface ZodIssue {
  path: (string | number)[];
  message: string;
}

export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | (ApiError & { error?: string; details?: ZodIssue[] })
      | undefined;

    // Parse Zod validation details into readable field-specific messages
    if (data?.details && Array.isArray(data.details) && data.details.length > 0) {
      const fieldErrors = data.details
        .map((d) => {
          const field = d.path?.length > 0 ? d.path.join(".") : "field";
          return `${field}: ${d.message}`;
        })
        .join("; ");
      return fieldErrors;
    }

    return data?.error || data?.message || error.message || "An unexpected error occurred";
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "An unexpected error occurred";
}

export default api;
